'use client';

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import RushDeliveryCard from './RushDeliveryCard';

export interface SummaryRow {
  label: string;
  value: string;
}

interface OrderSummaryProps {
  serviceName: string;
  rows: SummaryRow[];
  imagesUploaded: number;
  estimatedOutput: string;
  deliveryLabel: string;
  price: number;
  priceIsEstimate: boolean;
  rushEnabled: boolean;
  rushFee: number;
  onRushToggle: (v: boolean) => void;
  canContinue: boolean;
  continueLabel?: string;
  onContinue: () => void;
  isSubmitting?: boolean;
}

/** Animated number that gently transitions when the price updates. */
function AnimatedPrice({ value, isEstimate }: { value: number; isEstimate: boolean }) {
  return (
    <div className="flex items-baseline gap-1.5">
      {isEstimate && <span className="text-[11px] text-gray-500 font-medium">est.</span>}
      <AnimatePresence mode="popLayout">
        <motion.span
          key={value}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="text-3xl font-bold bg-gradient-to-r from-purple-300 to-purple-400 bg-clip-text text-transparent"
        >
          ${value.toFixed(2)}
        </motion.span>
      </AnimatePresence>
      <span className="text-xs text-gray-500 font-medium">USD</span>
    </div>
  );
}

export default function OrderSummary({
  serviceName,
  rows,
  imagesUploaded,
  estimatedOutput,
  deliveryLabel,
  price,
  priceIsEstimate,
  rushEnabled,
  rushFee,
  onRushToggle,
  canContinue,
  continueLabel = 'Continue to Review',
  onContinue,
  isSubmitting = false,
}: OrderSummaryProps) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-[#13151F]/80 backdrop-blur-sm p-5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center text-purple-300">
          <Icon name="ClipboardDocumentListIcon" size={18} variant="outline" />
        </div>
        <h3 className="text-base font-bold text-white">Order Summary</h3>
      </div>

      {/* Service */}
      <div className="flex items-center justify-between py-2.5 border-b border-white/[0.06]">
        <span className="text-xs text-gray-500">Service</span>
        <span className="text-sm font-semibold text-white text-right">{serviceName}</span>
      </div>

      {/* Preference rows */}
      <div className="py-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between py-1.5">
            <span className="text-xs text-gray-500">{r.label}</span>
            <span className="text-xs font-medium text-gray-200 text-right">{r.value}</span>
          </div>
        ))}
      </div>

      {/* Counts */}
      <div className="mt-1 pt-3 border-t border-white/[0.06] space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Images Uploaded</span>
          <span className="text-sm font-semibold text-white">{imagesUploaded}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Estimated Output</span>
          <span className="text-sm font-semibold text-white">{estimatedOutput}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Delivery</span>
          <span className="text-sm font-semibold text-white">{deliveryLabel}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Revisions</span>
          <span className="text-sm font-semibold text-emerald-300">Unlimited</span>
        </div>
      </div>

      {/* Price */}
      <div className="mt-4 pt-4 border-t border-white/[0.08] flex items-center justify-between">
        <span className="text-sm font-semibold text-white flex items-center gap-1">
          {priceIsEstimate ? 'Estimated Maximum' : 'Total Price'}
          {priceIsEstimate && (
            <span className="relative group/tip inline-flex items-center">
              <Icon
                name="InformationCircleIcon"
                size={14}
                variant="outline"
                className="text-gray-500 cursor-help"
              />
              <span className="pointer-events-none absolute bottom-full left-0 mb-2 w-60 rounded-lg bg-[#0B0D17] border border-white/[0.12] p-3 text-[11px] leading-relaxed text-gray-300 opacity-0 group-hover/tip:opacity-100 transition-opacity shadow-xl z-20">
                This is an estimate based on your upload. Our editors review every order, and if your images
                require less work than estimated, your final price is reduced before payment.
              </span>
            </span>
          )}
        </span>
        <AnimatedPrice value={price} isEstimate={priceIsEstimate} />
      </div>

      {/* Rush */}
      <div className="mt-4">
        <RushDeliveryCard enabled={rushEnabled} fee={rushFee} onToggle={onRushToggle} />
      </div>

      {/* CTA */}
      <motion.button
        type="button"
        whileHover={canContinue && !isSubmitting ? { y: -1 } : undefined}
        whileTap={canContinue && !isSubmitting ? { scale: 0.99 } : undefined}
        disabled={!canContinue || isSubmitting}
        onClick={onContinue}
        className={`w-full mt-4 py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-colors ${
          canContinue && !isSubmitting
            ? 'bg-gradient-to-r from-purple-600 to-purple-500 text-white shadow-lg shadow-purple-500/30 hover:from-purple-500 hover:to-purple-400'
            : 'bg-white/[0.04] text-gray-500 cursor-not-allowed'
        }`}
      >
        {isSubmitting ? (
          <>
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Processing...
          </>
        ) : (
          <>
            {continueLabel}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </>
        )}
      </motion.button>

      {/* trust */}
      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Icon name="ArrowPathIcon" size={14} variant="outline" className="text-gray-500" />
          Unlimited Revisions
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Icon name="ShieldCheckIcon" size={14} variant="outline" className="text-gray-500" />
          100% Satisfaction Guaranteed
        </div>
      </div>
    </div>
  );
}
